import React from 'react';
import {
  IQRCodeContentCommonProps,
  QRCodeContentNavigationActions,
  QRCodeScreenTypes,
} from '../../QRCodeCommonComponentEnums';
import QRCodeContentViewWrapper from '../QRCodeViewWrapper';
import PIEModal from '@src/shared/components/PIEModal';
import PIEText from '@src/shared/components/PIEText';
import { useTranslation } from 'react-i18next';
import {
  IconArrowLeft,
  IconChat,
  IconCopy,
  IconEmail,
  IconInfoCircle,
} from '@justeattakeaway/pie-icons-react';
import ConnectionCodeContainer from '../ConnectionCodeContainer';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import styles from './QRCodeShareContent.module.scss';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import IconWhatsApp from '@src/assets/whatsapp-icon.svg';
import DeliveryDashboardBottomSheet from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';

interface componentProps extends IQRCodeContentCommonProps {
  predefinedQRSize?: number;
  connectionCode: string;
}

const CourierCodeShareContent = ({
  bottomSheetView,
  popupView,
  onNavigate,
  connectionCode,
}: componentProps) => {
  const { t } = useTranslation();
  const { isMobileDevice } = useDeviceDetect();

  const onBackHandler = () => {
    onNavigate?.(QRCodeContentNavigationActions.JUMP_SCREEN, QRCodeScreenTypes.SHOW_QR_CODE);
  };

  const onCopyHandler = () => {
    navigator.clipboard.writeText(connectionCode);
    alert(t('ocf_screens.QR_CODE_COPY_SUCCESS_MESSAGE'));
  };

  const onSMSHandler = () => {
    const body = encodeURIComponent(connectionCode);
    window.location.href = `sms:?&body=${body}`;
  };

  const onEmailHandler = () => {
    const subject = encodeURIComponent(t('ocf_screens.QR_CODE_EMAIL_SUBJECT'));
    const body = encodeURIComponent(connectionCode);

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const onWhatsappShareHandler = () => {
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(connectionCode)}`;
    window.open(whatsappUrl, '_blank');
  };

  const renderContent = () => {
    return (
      <PIEComponentUtil paddingX="var(--xds-spacing-c)">
        <PIEText
          size={'var(--xds-font-size-14)'}
          fontWeight="var(--xds-font-weight-regular)"
          text={
            <>
              <div className={styles.informationContainer}>
                <IconInfoCircle size="xs" /> {t('ocf_screens.QR_CODE_SHARE_INFORMATION')}
              </div>
            </>
          }
        />
        <PIEComponentUtil marginTop={'var(--xds-spacing-e)'} marginBottom="var(--xds-spacing-c)">
          <ConnectionCodeContainer code={connectionCode} />
        </PIEComponentUtil>
        <PIEComponentUtil marginTop="var(--xds-spacing-d)">
          <div className={styles.shareOptions}>
            <div className={styles.shareOption}>
              <div className={styles.shareOptionIconContainer}>
                <img src={IconWhatsApp} alt="WhatsApp" onClick={onWhatsappShareHandler} />
              </div>
              <PIEText
                size={'var(--xds-font-size-12'}
                fontWeight="var(--xds-font-weight-regular)"
                text={t('ocf_screens.QR_CODE_SHARE_OPTION_WHATSAPP')}
                align="center"
              />
            </div>
            {isMobileDevice && (
              <div className={styles.shareOption}>
                <div className={styles.shareOptionIconContainer}>
                  <IconChat
                    onClick={onSMSHandler}
                    size="l"
                    color="var(--xds-color-interactive-brand)"
                  />
                </div>
                <PIEText
                  size={'var(--xds-font-size-12'}
                  fontWeight="var(--xds-font-weight-regular)"
                  text={t('ocf_screens.QR_CODE_SHARE_OPTION_SMS')}
                  align="center"
                />
              </div>
            )}
            <div className={styles.shareOption}>
              <div className={styles.shareOptionIconContainer}>
                <IconEmail
                  onClick={onEmailHandler}
                  size="l"
                  color="var(--xds-color-interactive-brand)"
                />
              </div>
              <PIEText
                size={'var(--xds-font-size-12'}
                fontWeight="var(--xds-font-weight-regular)"
                text={t('ocf_screens.QR_CODE_SHARE_OPTION_EMAIL')}
                align="center"
              />
            </div>
            <div className={styles.shareOption}>
              <div className={styles.shareOptionIconContainer}>
                <IconCopy
                  onClick={onCopyHandler}
                  size="l"
                  color="var(--xds-color-interactive-brand)"
                />
              </div>
              <PIEText
                size={'var(--xds-font-size-12'}
                fontWeight="var(--xds-font-weight-regular)"
                text={t('ocf_screens.QR_CODE_SHARE_OPTION_COPY')}
                align="center"
              />
            </div>
          </div>
        </PIEComponentUtil>
      </PIEComponentUtil>
    );
  };

  const renderDesktopView = (content: React.ReactNode) => {
    return (
      <PIEModal isOpen={true} size="small" modOverlay>
        <PIEModal.Header
          showBack
          onBackClick={onBackHandler}
          onClose={() => onNavigate?.(QRCodeContentNavigationActions.CLOSE)}
          heading={
            <PIEText
              size={'var(--xds-font-size-24)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_SHARE_TITLE')}
            />
          }
        />
        <PIEModal.Content>{content}</PIEModal.Content>
      </PIEModal>
    );
  };

  const renderMobileView = (
    content: React.ReactNode,
    overrideBlockingValue?: boolean | undefined
  ) => {
    return (
      <DeliveryDashboardBottomSheet
        blocking={overrideBlockingValue !== undefined ? overrideBlockingValue : true}
        constantHeight={350}
        onBottomSheetHeight={() => {}}
        showCloseButton
        onClose={onBackHandler}
        header={
          <div className={styles.mobileBottomSheetHeader}>
            <IconArrowLeft size="m" onClick={onBackHandler} />
            <PIEText
              align="left"
              size={'var(--xds-font-size-20)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_SHARE_TITLE')}
            />
          </div>
        }
      >
        <PIEComponentUtil padding="var(--xds-spacing-b)">{content}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    );
  };

  return (
    <QRCodeContentViewWrapper
      content={renderContent()}
      bottomSheetView={bottomSheetView}
      popupView={popupView}
      bottomSheetWrapper={renderMobileView}
      popupWrapper={renderDesktopView}
    />
  );
};

export default CourierCodeShareContent;
